import React, { useState } from 'react';
import { TrendingUp, BarChart2, DollarSign, Clock, ArrowRight, LineChart, Info } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения анализа ROI и эффективности внедрения ИИ
 */
const RoiAnalysisDashboard = ({ results, formData }) => {
  // Активная вкладка
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!results) return null;

  // Функция для форматирования денежных значений
  const formatMoney = (value) => {
    return formatCurrency(value, 0, results.currency);
  };

  // Расчет накопленного эффекта по годам
  const calculateCumulativeEffect = (years) => {
    const initialInvestment = results.totalImplementationCost;
    const annualMaintenance = results.annualMaintenanceCost;
    const annualBenefit = results.annualBenefit;
    
    // Для каждого года рассчитываем накопленный эффект
    const yearlyData = [];
    let cumulativeEffect = -initialInvestment;
    
    for (let year = 1; year <= years; year++) {
      cumulativeEffect += annualBenefit - annualMaintenance;
      yearlyData.push({
        year,
        cumulativeEffect,
        isPositive: cumulativeEffect > 0,
        maintenance: annualMaintenance * year,
        benefit: annualBenefit * year,
        roi: ((annualBenefit * year - initialInvestment - annualMaintenance * year) / initialInvestment) * 100
      });
    }
    
    return yearlyData;
  };

  // Получение накопленного эффекта за 5 лет
  const fiveYearEffect = calculateCumulativeEffect(5);

  // Расчет точки безубыточности (в месяцах)
  const calculateBreakEvenPoint = () => {
    if (results.annualBenefit <= results.annualMaintenanceCost) {
      return null; // Если годовой эффект меньше затрат на поддержку, проект никогда не окупится
    }
    
    const monthlyBenefit = results.annualBenefit / 12;
    const monthlyMaintenance = results.annualMaintenanceCost / 12;
    const netMonthlyBenefit = monthlyBenefit - monthlyMaintenance;
    
    // Количество месяцев до покрытия начальных инвестиций
    return Math.ceil(results.totalImplementationCost / netMonthlyBenefit);
  };

  const breakEvenPointMonths = calculateBreakEvenPoint();

  // Функция для форматирования периода окупаемости
  const formatBreakEvenPeriod = (months) => {
    if (months === null) return "Не окупается";
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${remainingMonths} мес.`;
    } else if (remainingMonths === 0) {
      return `${years} ${formatYearsLabel(years)}`;
    } else {
      return `${years} ${formatYearsLabel(years)} и ${remainingMonths} мес.`;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8 mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-emerald-500" />
          Анализ эффективности инвестиций
        </h2>
      </div>

      {/* Вкладки для навигации */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'overview'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Ключевые показатели
        </button>
        <button
          onClick={() => setActiveTab('forecast')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'forecast'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Прогноз на 5 лет
        </button>
      </div>

      {/* Вкладка с обзором ключевых показателей */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Основные метрики */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-emerald-500" />
                <h3 className="text-emerald-700 dark:text-emerald-300 font-medium">Срок окупаемости</h3>
              </div>
              <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                {formatBreakEvenPeriod(breakEvenPointMonths)}
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                Точка безубыточности проекта
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-4 border border-blue-100 dark:border-blue-800/50 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <h3 className="text-blue-700 dark:text-blue-300 font-medium">ROI за 3 года</h3>
              </div>
              <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                {formatNumber(results.threeYearROI, 1)}%
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Рентабельность инвестиций
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg p-4 border border-purple-100 dark:border-purple-800/50 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                <h3 className="text-purple-700 dark:text-purple-300 font-medium">Годовой эффект</h3>
              </div>
              <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                {formatMoney(results.annualBenefit)}
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                Экономия и дополнительные доходы
              </p>
            </div>
          </div>

          {/* Разбивка финансового эффекта */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-indigo-500" />
              Структура годового финансового эффекта
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300">Экономия затрат</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{formatMoney(results.costSavings)}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="h-2.5 rounded-full bg-emerald-500 dark:bg-emerald-400" 
                    style={{ width: `${(results.costSavings / results.annualBenefit) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Снижение текущих расходов на образовательный процесс
                </p>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300">Дополнительная прибыль</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{formatMoney(results.additionalRevenue)}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="h-2.5 rounded-full bg-blue-500 dark:bg-blue-400" 
                    style={{ width: `${(results.additionalRevenue / results.annualBenefit) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Рост доходов благодаря повышению качества образования
                </p>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300">Ежегодные расходы на поддержку (минус)</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">- {formatMoney(results.annualMaintenanceCost)}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="h-2.5 rounded-full bg-red-500 dark:bg-red-400" 
                    style={{ width: `${(results.annualMaintenanceCost / results.annualBenefit) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Затраты на техническое обслуживание и поддержку системы
                </p>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Чистый годовой эффект</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{formatMoney(results.annualBenefit - results.annualMaintenanceCost)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Информация о факторах, влияющих на ROI */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 border border-blue-100 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <div>
                <Info className="w-5 h-5 text-blue-500 mt-1" />
              </div>
              <div>
                <h4 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-2">Факторы, влияющие на ROI</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-blue-600 dark:text-blue-400">
                  <li>Ожидаемое повышение эффективности: {formData.expectedEfficiencyGain}%</li>
                  <li>Текущие затраты на одного студента: {formatMoney(parseFloat(formData.existingCostsPerStudent))}</li>
                  <li>Уровень влияния на доходы: {getRevenueImpactName(formData.revenueImpact)}</li>
                  <li>Фактор снижения затрат: {getCostReductionName(formData.costReductionFactor)}</li>
                  <li>Период внедрения: {formData.implementationPeriod} мес.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Вкладка с прогнозом на 5 лет */}
      {activeTab === 'forecast' && (
        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-indigo-500" />
              Финансовый прогноз на 5 лет
            </h3>

            {/* Визуализация накопленного эффекта по годам */}
            <div className="relative h-80 mt-6 mb-8">
              {/* Горизонтальная линия - ось X */}
              <div className="absolute left-0 right-0 top-1/2 h-px bg-gray-300 dark:bg-gray-600"></div>
              
              {/* Вертикальная шкала */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-300 dark:bg-gray-600"></div>
              
              {/* Метки для оси Y */}
              <div className="absolute left-2 top-0 text-xs text-gray-500 dark:text-gray-400">
                +{formatMoney(Math.max(...fiveYearEffect.map(d => Math.abs(d.cumulativeEffect))))}
              </div>
              <div className="absolute left-2 bottom-0 text-xs text-gray-500 dark:text-gray-400">
                -{formatMoney(Math.max(...fiveYearEffect.map(d => Math.abs(d.cumulativeEffect))))}
              </div>
              <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                0
              </div>
              
              {/* Столбцы для каждого года */}
              <div className="absolute inset-0 flex justify-around items-center pt-10 pb-10">
                {fiveYearEffect.map((yearData, index) => {
                  // Нормализуем высоту относительно максимального значения
                  const maxValue = Math.max(...fiveYearEffect.map(d => Math.abs(d.cumulativeEffect)));
                  const heightPercentage = (Math.abs(yearData.cumulativeEffect) / maxValue) * 100;
                  
                  // Определяем смещение в зависимости от знака (положительный - вверх, отрицательный - вниз)
                  const isPositive = yearData.cumulativeEffect >= 0;
                  
                  return (
                    <div key={index} className="flex flex-col items-center relative h-full">
                      <div 
                        className={`w-16 absolute ${isPositive ? 'bottom-1/2' : 'top-1/2'}`}
                        style={{ 
                          height: `${heightPercentage / 2}%`,
                          backgroundColor: isPositive ? '#10b981' : '#ef4444',
                          borderRadius: '4px'
                        }}
                      ></div>
                      
                      <div className="absolute bottom-0 text-xs font-medium text-gray-700 dark:text-gray-300">
                        Год {yearData.year}
                      </div>
                      
                      <div className={`absolute ${isPositive ? 'bottom-1/2' : 'top-1/2'} mt-2 mb-2 text-xs font-medium ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {isPositive ? '+' : ''}{formatMoney(yearData.cumulativeEffect)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Таблица прогноза по годам */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mt-6">
              <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-semibold">Год</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-right">Затраты на поддержку</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-right">Финансовый эффект</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-right">Накопленный эффект</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-right">ROI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr className="bg-gray-50 dark:bg-gray-800/70">
                    <td className="px-4 py-3 font-medium">Инвестиции</td>
                    <td className="px-4 py-3 text-right">-</td>
                    <td className="px-4 py-3 text-right">-</td>
                    <td className="px-4 py-3 text-right text-red-600 dark:text-red-400 font-medium">
                      -{formatMoney(results.totalImplementationCost)}
                    </td>
                    <td className="px-4 py-3 text-right">-</td>
                  </tr>
                  {fiveYearEffect.map((yearData, index) => (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        yearData.isPositive ? 'bg-emerald-50/30 dark:bg-emerald-900/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">Год {yearData.year}</td>
                      <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">
                        -{formatMoney(results.annualMaintenanceCost)}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400">
                        +{formatMoney(results.annualBenefit)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        <span className={yearData.isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                          {yearData.isPositive ? '+' : ''}{formatMoney(yearData.cumulativeEffect)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={yearData.roi >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                          {formatNumber(yearData.roi, 1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Примечание о точке окупаемости */}
            <div className="mt-4 bg-gray-100 dark:bg-gray-700/30 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
              <p className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-emerald-500" />
                <span>
                  <strong>Точка окупаемости:</strong> {formatBreakEvenPeriod(breakEvenPointMonths)}
                  {breakEvenPointMonths !== null && ` (${(breakEvenPointMonths / 12).toFixed(1)} года)`}
                </span>
              </p>
              <p className="mt-2 text-xs italic">
                Прогноз основан на предположении о стабильном финансовом эффекте на протяжении всего периода.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

// Получение названия влияния на доход
const getRevenueImpactName = (id) => {
  const labels = {
    'minimal': 'Минимальное',
    'moderate': 'Умеренное',
    'significant': 'Значительное'
  };
  return labels[id] || id;
};

// Получение названия фактора снижения затрат
const getCostReductionName = (id) => {
  const labels = {
    'low': 'Низкий',
    'medium': 'Средний',
    'high': 'Высокий'
  };
  return labels[id] || id;
};

export default RoiAnalysisDashboard;